import { useTranslation } from "react-i18next";
import { useGetShortLink } from "../hooks/use-get-short-link";
import { useFormShortLink } from "../hooks/use-form-short-link";
import { Card, Col, Form, Input, Row, Spin, Typography } from "antd";
import { Radio } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { openNotification } from "../../../utils/helpers";
import { ShortLinkFormData } from "./interface";
import VuiPageTitle from "../../../components/page-title";
import VuiFormActionButton from "../../../components/form-action-button";

const { Text } = Typography;

const ShortLinkFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const title = id ? t("pages.shortLink.edit.title") : t("pages.shortLink.add.title");
  const {
    isOnFetchingDetailData,
    loadDetailData,
    detailData
  } = useGetShortLink();
  const { submitLoading, onSubmit, onDelete, deleteLoading } = useFormShortLink();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [form] = Form.useForm();

  const onFinish = useCallback(
    async (data: ShortLinkFormData) => {
      const normalizeData: ShortLinkFormData = {
        ...data,
      };


      await onSubmit(normalizeData, id);
    }, [onSubmit, id]
  );

  useEffect(() => {
    if (id) {
      loadDetailData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (detailData) {
      form.setFieldsValue({
        slug: detailData.slug,
        original_url: detailData.original_url,
        is_active: detailData.is_active,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData]);

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.shortLink"),
          })
        );
        navigate(`/short-link`);
      });
    }
  }, [id, navigate, onDelete, t]);

  return (
    <>
      <VuiPageTitle
        title={title}
        onBackLink={"/short-link"}
      />

      <Spin size="large" spinning={isOnFetchingDetailData}>
        <Form
          form={form}
          layout={"vertical"}
          initialValues={{
            is_active: true,
          }}
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Card>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <Form.Item
                      name="slug"
                      label={t("common.form.slug.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.slug.label"),
                          }),
                        },
                        {
                          pattern: /^[A-Za-z0-9-]+$/,
                          message: t("validation.slug"),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.slug.placeholder")}
                      />
                    </Form.Item>

                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="original_url"
                      label={t("common.form.originalURL.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.originalURL.label"),
                          }),
                        },
                        {
                          validator: async (_: any, value: string) => {
                            if (!value) return Promise.resolve();
                            if (/^https?:\/\//i.test(value)) return Promise.resolve();
                            return Promise.reject(new Error(t("validation.urlProtocol")));
                          },
                        },
                      ]}
                    >
                      <Input
                        type="url"
                        size="large"
                        placeholder={t("common.form.originalURL.placeholder")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card title={t("common.text.status")}>
                <Form.Item name="is_active">
                  <Radio.Group>
                    <Radio value={true}>{t("common.text.active")}</Radio>
                    <Radio value={false}>{t("common.text.inactive")}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24}>
              <VuiFormActionButton
                cancelBtnLink={"/short-link"}
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="short-link.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin >
      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
}

export default ShortLinkFormModule;